/**
 * Skills Tab
 * Manages skill configurations for Response Classifier with AI-assisted generation
 */

import React, { useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { SkillEditor } from "@/features/skills/components/SkillEditor";
import {
  Plus,
  Zap,
  Settings2,
  Info,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { useI18n } from "@/i18n";
import type { SkillConfig } from "@/types";
import { useSkillValidation } from "../hooks/useSkillValidation";
import { SkillGlobalSettingsDrawer } from "./SkillGlobalSettingsDrawer";

// Default skill tool instructions (synced with agents/core/hooks/classifier/hook.py)
const DEFAULT_SKILL_TOOL_INSTRUCTIONS = `你可以使用以下技能工具：

1. get_skill_instructions(skill_id) - 获取技能的完整执行指令
   当你需要执行某个技能但没有自动加载指令时，可以调用此工具获取。
   参数: skill_id (如 "annual_report", "chart_overview")

2. list_available_skills() - 列出所有可用技能
   查看当前agent配置的所有技能及其描述。

使用场景：
- 当 <available_skills> 中列出的技能没有被自动匹配时
- 当你需要查看某个技能的详细指令时
- 当用户明确要求使用某个技能时`;

interface SkillDedupValues {
  enabled?: boolean;
  data_query_hours?: number | null;
  report_window_hours?: number | null;
  max_per_window?: number | null;
  output_preview_length?: number | null;
  show_user_query?: boolean | null;
  query_preview_length?: number | null;
}

interface SkillsTabProps {
  skills: SkillConfig[];
  onSkillsChange: (skills: SkillConfig[]) => void;
  // Classifier settings (read-only, controlled from Response Classification view)
  classifierEnabled: boolean;
  classifierVisible?: boolean;
  // Skills settings
  skillsEnabled: boolean;
  onSkillsEnabledChange: (enabled: boolean) => void;
  semanticMatchingEnabled: boolean;
  onSemanticMatchingEnabledChange: (enabled: boolean) => void;
  semanticSimilarityThreshold: number;
  onSemanticSimilarityThresholdChange: (threshold: number) => void;
  // Rule confidence threshold
  ruleConfidenceThreshold: number;
  onRuleConfidenceThresholdChange: (threshold: number) => void;
  // LLM settings for skills
  skillLlmTimeout: number;
  onSkillLlmTimeoutChange: (timeout: number) => void;
  skillToolInstructions: string;
  onSkillToolInstructionsChange: (instructions: string) => void;
  // Skill dedup settings (global)
  dedupConfig?: SkillDedupValues;
  onDedupConfigChange?: (field: string, value: boolean | number | null) => void;
  // Whether skill_tracking is enabled (required for dedup)
  skillTrackingEnabled?: boolean;
  // Context
  agentContext?: string;
  // Classification rules
  llmFallbackEnabled?: boolean;
  onLlmFallbackEnabledChange?: (enabled: boolean) => void;
  customQuickKeywords?: string[];
  onCustomQuickKeywordsChange?: (keywords: string[]) => void;
  customQuickPatterns?: string[];
  onCustomQuickPatternsChange?: (patterns: string[]) => void;
}

const DEFAULT_SKILL: Omit<SkillConfig, "skill_id"> = {
  name: "新技能",
  description: "",
  enabled: true,
  priority: 100,
  exclusion_group: null,
  keywords: [],
  patterns: [],
  example_queries: [],
  instructions: "",
  force_response_type: null,
  display_row: 0,
};

// ─── Built-in classifier rules (mirrored from response_classifier.py) ───
// Max message length for rule-based QUICK classification
const BUILTIN_MAX_LENGTH = 50;

const BUILTIN_RULES = [
  {
    key: "greeting" as const,
    confidence: 0.95,
    patterns: [
      String.raw`^(你好|嗨|哈喽|hello|hi|hey|早上好|下午好|晚上好|早安|午安|晚安)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～？?]*$`,
      String.raw`^(在吗|在不在|有人吗)[呀啊哇嘛哦呢啦吖吗嘞]?[\s?？]*$`,
      String.raw`^(good\s*(morning|afternoon|evening|night)|howdy|yo)[\s!.]*$`,
    ],
  },
  {
    key: "acknowledgment" as const,
    confidence: 0.95,
    patterns: [
      String.raw`^(好的?|行|可以|没问题|知道了|明白了?|收到|ok|okay|嗯+|哦+|是的?|对的?)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～？?]*$`,
      String.raw`^(谢谢|感谢|多谢|thx|thanks|thank you)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～？?]*$`,
      String.raw`^(yes|no|yeah|yep|nope)[\s!.]*$`,
      String.raw`^(行吧|好吧)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～？?]*$`,
    ],
  },
  {
    key: "casual_chat" as const,
    confidence: 0.85,
    patterns: [
      String.raw`^(今天天气.{0,10}|天气.{0,5})[\s?？]*$`,
      String.raw`^(现在几点|几点了)[\s?？]*$`,
      String.raw`^(你呢|怎么样|还好吗)[呀啊哇嘛哦呢啦吖吗嘞]?[\s?？]*$`,
    ],
  },
  {
    key: "laughter" as const,
    confidence: 0.9,
    patterns: [
      String.raw`^(哈[哈]+|嘿[嘿]+|嘻[嘻]+|呵[呵]+|噗[噗]*|哈哈哈+)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～]*$`,
      String.raw`^(lol|haha|hehe|lmao|lmfao|rofl)[\s!.]*$`,
    ],
  },
  {
    key: "farewell" as const,
    confidence: 0.9,
    patterns: [
      String.raw`^(再见|拜拜|拜|回见|下次见|下次聊|改天聊|先走了|先下了)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～]*$`,
      String.raw`^(bye|goodbye|see you|see ya|cya|ttyl|gtg|gotta go)[\s!.~]*$`,
    ],
  },
  {
    key: "reaction" as const,
    confidence: 0.85,
    patterns: [
      String.raw`^(666+|yyds|awsl|xswl|orz)[\s!！。.~～]*$`,
      String.raw`^(厉害|牛|棒|赞|绝了|无语|哇塞|天哪|服了|真的假的)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～]*$`,
      String.raw`^(算了|也行|随便|无所谓|都行|随意)[呀啊哇嘛哦呢啦吖吗嘞]?[\s!！。.~～]*$`,
    ],
  },
];

const BUILTIN_LABEL_MAP: Record<string, string> = {
  greeting: "builtinGreeting",
  acknowledgment: "builtinAck",
  casual_chat: "builtinCasual",
  laughter: "builtinLaughter",
  farewell: "builtinFarewell",
  reaction: "builtinReaction",
};

const BUILTIN_LABEL_FALLBACK: Record<string, string> = {
  greeting: "问候",
  acknowledgment: "确认/感谢",
  casual_chat: "闲聊",
  laughter: "笑声",
  farewell: "告别",
  reaction: "反应/感叹",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function BuiltinRulesReference({ skillsT }: { skillsT?: Record<string, any> }) {
  const [expanded, setExpanded] = useState(false);
  const t = (key: string, fallback: string) => {
    const v = skillsT?.[key];
    return typeof v === "string" ? v : fallback;
  };
  return (
    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left text-sm"
      >
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {t("builtinRules", "系统内置规则")}
        </span>
        <span className="text-xs text-gray-500">
          {t("builtinRulesDesc", "以下规则始终生效，自定义规则在内置规则之后检查。")}
        </span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Length limit notice */}
          <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-700">
            <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-amber-700 dark:text-amber-300">
              {t("builtinMaxLength", `仅当消息长度 ≤ ${BUILTIN_MAX_LENGTH} 字符时才进行规则匹配，超过此长度直接进入 DEEP 路径。`)}
            </span>
          </div>
          {/* Skill reassurance notice */}
          <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-blue-700 dark:text-blue-300">
              {t("builtinSkillNote", "快速回复仅跳过技能匹配流程和重型工具（MCP、记忆工具等），但技能工具始终保留。Agent 在快速回复中也可通过 get_skill_instructions 主动查询并调用已配置的技能。")}
            </span>
          </div>
          {BUILTIN_RULES.map((rule) => (
            <div
              key={rule.key}
              className="p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-md space-y-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  {t(BUILTIN_LABEL_MAP[rule.key], BUILTIN_LABEL_FALLBACK[rule.key])}
                </span>
                <span className="text-[10px] text-gray-400">
                  confidence: {rule.confidence}
                </span>
              </div>
              <div className="space-y-1">
                {rule.patterns.map((pat, i) => (
                  <code
                    key={i}
                    className="block text-[11px] px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded font-mono text-gray-600 dark:text-gray-300 break-all"
                  >
                    {pat}
                  </code>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const SkillsTab: React.FC<SkillsTabProps> = ({
  skills,
  onSkillsChange,
  classifierEnabled,
  classifierVisible = true,
  skillsEnabled,
  onSkillsEnabledChange,
  semanticMatchingEnabled,
  onSemanticMatchingEnabledChange,
  semanticSimilarityThreshold,
  onSemanticSimilarityThresholdChange,
  ruleConfidenceThreshold,
  onRuleConfidenceThresholdChange,
  skillLlmTimeout,
  onSkillLlmTimeoutChange,
  skillToolInstructions,
  onSkillToolInstructionsChange,
  dedupConfig,
  onDedupConfigChange,
  skillTrackingEnabled = false,
  agentContext,
  llmFallbackEnabled,
  onLlmFallbackEnabledChange,
  customQuickKeywords,
  onCustomQuickKeywordsChange,
  customQuickPatterns,
  onCustomQuickPatternsChange,
}) => {
  const { messages } = useI18n();
  const skillsT = messages.agentConfig.skills;

  // Counter for generating unique temporary IDs
  const tempKeyCounter = useRef(0);

  // Skill validation
  const {
    isValid,
    errors,
    warnings,
    isValidating,
    getSkillErrors,
    getSkillWarnings,
  } = useSkillValidation(skills, { debounceMs: 800 });

  // Help section expanded state
  const [helpExpanded, setHelpExpanded] = useState(false);

  // Global settings drawer state
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(
    skills.length > 0 ? skills[0].skill_id : null,
  );

  const selectedSkill = skills.find((s) => s.skill_id === selectedSkillId);

  // Group skills by exclusion_group
  const groupedSkills = skills.reduce(
    (acc, skill) => {
      const group = skill.exclusion_group || "_ungrouped";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(skill);
      return acc;
    },
    {} as Record<string, SkillConfig[]>,
  );

  const handleAddSkill = useCallback(() => {
    const tempId = `skill_${skills.length + 1}_${tempKeyCounter.current++}`;
    const newSkillName = (skillsT?.newSkillName || "技能 {index}").replace(
      "{index}",
      String(skills.length + 1),
    );
    const newSkill: SkillConfig = {
      ...DEFAULT_SKILL,
      skill_id: tempId,
      name: newSkillName,
    };
    onSkillsChange([...skills, newSkill]);
    setSelectedSkillId(newSkill.skill_id);
  }, [skills, onSkillsChange, skillsT]);

  const handleUpdateSkill = useCallback(
    (skillId: string, updates: Partial<SkillConfig>) => {
      const newSkillId = updates.skill_id;
      onSkillsChange(
        skills.map((s) => (s.skill_id === skillId ? { ...s, ...updates } : s)),
      );
      if (newSkillId && newSkillId !== skillId) {
        setSelectedSkillId(newSkillId);
      }
    },
    [skills, onSkillsChange],
  );

  const handleDeleteSkill = useCallback(
    (skillId: string) => {
      const newSkills = skills.filter((s) => s.skill_id !== skillId);
      onSkillsChange(newSkills);
      if (selectedSkillId === skillId) {
        setSelectedSkillId(newSkills.length > 0 ? newSkills[0].skill_id : null);
      }
    },
    [skills, selectedSkillId, onSkillsChange],
  );

  // Check if classifier is visible
  if (!classifierVisible) {
    return (
      <Card className="p-8 text-center">
        <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          {skillsT?.skillsNotAvailable || "技能不可用"}
        </h3>
        <p className="text-gray-500">
          {skillsT?.skillsHookHidden || "该 Agent 已隐藏技能 Hook。"}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Classifier disabled warning */}
      {!classifierEnabled && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">
                {skillsT?.classifierDisabledTitle || "分类引擎已关闭"}
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                {skillsT?.classifierDisabledDesc || "技能匹配运行在分类引擎内部。分类引擎关闭时，技能配置不会生效。请在 Hooks 面板中启用「分类引擎」。"}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* How classification and skill matching work together */}
      <Card className="px-4 py-3">
        <p className="text-sm text-gray-500 leading-relaxed">
          {skillsT?.classifierRelationDesc || "技能匹配与响应分类共用同一分类引擎：用户消息先经规则匹配（关键词/正则），再经语义匹配（向量检索），置信度不足时由 LLM 同时判断响应类型和技能，一次调用完成两件事。"}
        </p>
      </Card>

      {/* Settings Panel - Constrained width for better wide-screen experience */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">
            {skillsT?.skillMatchingSettings || "技能匹配设置"}
          </h3>
        </div>

        {/* Two-column layout: Switches | Thresholds */}
        <div className="max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Toggle Switches */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {skillsT?.featureToggles || "功能开关"}
            </h4>

            {/* Enable Skills */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="skills-enabled"
                checked={skillsEnabled}
                onChange={(e) => onSkillsEnabledChange(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">
                  {skillsT?.enableSkills || "启用技能"}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  {skillsT?.enableSkillsHint || "根据用户意图动态注入指令"}
                </span>
              </div>
            </label>

            {/* Enable Semantic Matching */}
            <label
              className={`flex items-center gap-3 cursor-pointer ${!skillsEnabled ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                id="semantic-matching-enabled"
                checked={semanticMatchingEnabled}
                onChange={(e) =>
                  onSemanticMatchingEnabledChange(e.target.checked)
                }
                disabled={!skillsEnabled}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">
                  {skillsT?.semanticMatching || "语义匹配"}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  {skillsT?.semanticMatchingHint || "TEI 服务（bge-m3）"}
                </span>
              </div>
            </label>
          </div>

          {/* Right: Threshold Sliders */}
          <div
            className={`space-y-4 p-4 bg-gray-50 rounded-lg ${!skillsEnabled ? "opacity-50" : ""}`}
          >
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {skillsT?.thresholdSettings || "阈值设置"}
            </h4>

            {/* Rule Confidence */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700">
                  {skillsT?.ruleConfidence || "规则置信度"}
                </span>
                <span className="text-sm font-mono text-gray-600">
                  {ruleConfidenceThreshold.toFixed(2)}
                </span>
              </div>
              <input
                type="range"
                id="rule-confidence"
                min="0"
                max="1"
                step="0.05"
                value={ruleConfidenceThreshold}
                onChange={(e) =>
                  onRuleConfidenceThresholdChange(parseFloat(e.target.value))
                }
                disabled={!skillsEnabled}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
            </div>

            {/* Semantic Similarity - only when enabled */}
            {semanticMatchingEnabled && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">
                    {skillsT?.semanticSimilarity || "语义相似度"}
                  </span>
                  <span className="text-sm font-mono text-gray-600">
                    {semanticSimilarityThreshold.toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  id="similarity-threshold"
                  min="0"
                  max="1"
                  step="0.05"
                  value={semanticSimilarityThreshold}
                  onChange={(e) =>
                    onSemanticSimilarityThresholdChange(
                      parseFloat(e.target.value),
                    )
                  }
                  disabled={!skillsEnabled || !semanticMatchingEnabled}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>
            )}

            {/* LLM Timeout */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700">
                  {skillsT?.llmTimeout || "LLM 超时"}
                </span>
                <span className="text-sm font-mono text-gray-600">
                  {skillLlmTimeout.toFixed(1)}s
                </span>
              </div>
              <input
                type="range"
                id="skill-llm-timeout"
                min="1"
                max="30"
                step="0.5"
                value={skillLlmTimeout}
                onChange={(e) =>
                  onSkillLlmTimeoutChange(parseFloat(e.target.value))
                }
                disabled={!skillsEnabled}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
            </div>
          </div>
        </div>

        {/* Skill Tool Instructions - Collapsible */}
        <details
          className={`mt-4 ${!skillsEnabled ? "opacity-50 pointer-events-none" : ""}`}
        >
          <summary className="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700 hover:text-gray-900">
            {skillsT?.skillToolInstructions || "技能工具使用说明"}
            {skillToolInstructions ? (
              <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                {skillsT?.customized || "已自定义"}
              </span>
            ) : (
              <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                {skillsT?.default || "默认"}
              </span>
            )}
          </summary>
          <div className="mt-3 max-w-3xl">
            <div className="flex items-center justify-end mb-2">
              {skillToolInstructions && (
                <button
                  type="button"
                  onClick={() => onSkillToolInstructionsChange("")}
                  disabled={!skillsEnabled}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={skillsT?.resetToDefault || "重置为默认说明"}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {skillsT?.resetToDefault || "重置为默认"}
                </button>
              )}
            </div>
            <textarea
              id="skill-tool-instructions"
              value={skillToolInstructions || DEFAULT_SKILL_TOOL_INSTRUCTIONS}
              onChange={(e) => {
                const newValue =
                  e.target.value === DEFAULT_SKILL_TOOL_INSTRUCTIONS
                    ? ""
                    : e.target.value;
                onSkillToolInstructionsChange(newValue);
              }}
              disabled={!skillsEnabled}
              className={`w-full h-40 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm font-mono ${
                !skillToolInstructions ? "text-gray-500" : ""
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {skillsT?.skillToolInstructionsHint ||
                "工具 get_skill_instructions、list_available_skills 的使用说明，注入到系统提示词。"}
            </p>
          </div>
        </details>
      </Card>

      {/* Classification Rules Section */}
      {skillsEnabled && (
        <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Settings2 className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {skillsT?.classificationRules || "分类规则"}
            </h3>
            <span className="text-xs text-gray-500">{skillsT?.classificationRulesDesc || ""}</span>
          </div>

          {/* Default patterns info */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {skillsT?.defaultPatternsInfo || "系统默认规则已覆盖常见问候、确认、闲聊等模式。"}
              </p>
            </div>
          </div>

          {/* LLM Fallback Toggle */}
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {skillsT?.llmFallbackEnabled || "LLM 分类回退"}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {skillsT?.llmFallbackEnabledHint || ""}
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={llmFallbackEnabled ?? true}
                onChange={(e) => onLlmFallbackEnabledChange?.(e.target.checked)}
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
            </label>
          </div>

          {/* Custom Quick Keywords */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {skillsT?.customQuickKeywords || "自定义快速回复关键词"}
            </label>
            <p className="text-xs text-gray-500 mb-2">{skillsT?.customQuickKeywordsHint || ""}</p>
            <TagInput
              tags={customQuickKeywords || []}
              onChange={(tags) => onCustomQuickKeywordsChange?.(tags)}
              placeholder={skillsT?.customQuickKeywordsPlaceholder || "输入关键词后按回车"}
            />
          </div>

          {/* Custom Quick Patterns */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {skillsT?.customQuickPatterns || "自定义快速回复模式 (正则)"}
            </label>
            <p className="text-xs text-gray-500 mb-2">{skillsT?.customQuickPatternsHint || ""}</p>
            <PatternListInput
              patterns={customQuickPatterns || []}
              onChange={(patterns) => onCustomQuickPatternsChange?.(patterns)}
              placeholder={skillsT?.customQuickPatternsPlaceholder || "例: ^(查余额|签到)[呀啊]?$"}
              addLabel={skillsT?.addPattern || "添加模式"}
              invalidLabel={skillsT?.invalidRegex || "无效的正则表达式"}
            />
          </div>

          {/* Built-in Rules Reference */}
          <BuiltinRulesReference skillsT={skillsT} />
        </div>
      )}

      {/* How It Works - Collapsible Help Section */}
      <Card className="p-4">
        <button
          onClick={() => setHelpExpanded(!helpExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-500" />
            <h3 className="font-medium text-gray-900">
              {skillsT?.howItWorks || "技能匹配原理"}
            </h3>
          </div>
          {helpExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {helpExpanded && (
          <div className="mt-4 space-y-4 text-sm text-gray-600">
            {/* Three-Layer Matching */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                {skillsT?.threeLayerMatching || "三层级联匹配"}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="font-medium text-emerald-800 mb-1">
                    {skillsT?.layer1Rule || "第一层：规则匹配（<10ms）"}
                  </div>
                  <p className="text-emerald-700 text-xs">
                    {skillsT?.layer1RuleDesc ||
                      "匹配 keywords（0.85-0.95 置信度）和 patterns（0.9 置信度）。置信度 ≥ rule_confidence_threshold 时返回结果。"}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 mb-1">
                    {skillsT?.layer2Semantic || "第二层：语义匹配（~50ms）"}
                  </div>
                  <p className="text-blue-700 text-xs">
                    {skillsT?.layer2SemanticDesc ||
                      "需启用 semantic_matching_enabled。使用 TEI 服务（BAAI/bge-m3）在 Qdrant 中匹配 name、description 和 example_queries。相似度 ≥ similarity_threshold 时返回结果。"}
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="font-medium text-amber-800 mb-1">
                    {skillsT?.layer3Llm || "第三层：LLM 兜底（~200ms）"}
                  </div>
                  <p className="text-amber-700 text-xs">
                    {skillsT?.layer3LlmDesc ||
                      "使用 model_id 进行意图分类。超时：skill_llm_timeout 秒。当规则和语义匹配都失败时的兜底方案。"}
                  </p>
                </div>
              </div>
            </div>

            {/* Exclusion Groups */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">
                {skillsT?.exclusionGroupExplain || "互斥组解析"}
              </h4>
              <ul className="text-purple-700 text-xs space-y-1 list-disc list-inside">
                {(
                  skillsT?.exclusionGroupRules || [
                    "同一 exclusion_group 中的技能互斥",
                    "当同组多个技能匹配时，只保留 priority 最高的",
                    "不同组的技能可以同时匹配",
                    "没有设置 exclusion_group 的技能匹配后始终包含",
                  ]
                ).map((rule: string, index: number) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
            </div>

            {/* Field Purposes */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                {skillsT?.fieldPurposes || "技能配置字段说明"}
              </h4>
              <p className="text-gray-500 text-xs">
                {skillsT?.fieldPurposesDesc ||
                  "keywords/patterns → 规则匹配 | name/description/example_queries → 语义匹配 | instructions → 执行指令"}
              </p>
            </div>

            {/* Skill Tools */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-2">
                {skillsT?.skillTools || "技能工具（注入系统提示词）"}
              </h4>
              <p className="text-gray-600 text-xs mb-2">
                {skillsT?.skillToolsDesc ||
                  "启用技能后，Agent 可使用以下工具："}
              </p>
              <ul className="text-gray-600 text-xs space-y-1 list-disc list-inside">
                {(
                  skillsT?.skillToolsList || [
                    "get_skill_instructions(skill_id) - 获取指定技能的详细指令",
                    "list_available_skills() - 列出所有可用技能及描述",
                  ]
                ).map((item: string, index: number) => (
                  <li key={index}>
                    <code className="bg-gray-200 px-1 rounded">
                      {item.split(" - ")[0]}
                    </code>{" "}
                    - {item.split(" - ")[1]}
                  </li>
                ))}
              </ul>
              <p className="text-gray-500 text-xs mt-2">
                {skillsT?.skillToolsNote ||
                  "可在上方「技能工具使用说明」中自定义工具使用说明。"}
              </p>
            </div>

            {/* Example Queries Best Practices */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">
                {skillsT?.exampleQueriesBestPractices ||
                  "编写示例查询的最佳实践"}
              </h4>
              <ul className="text-blue-700 text-xs space-y-1 list-disc list-inside">
                {(
                  skillsT?.exampleQueriesTips || [
                    "每个技能编写 3-10 个不同的示例，覆盖用户的不同提问方式",
                    "包含变体：正式/口语、简短/详细、不同表达方式",
                    "聚焦于用户意图，而非技术术语",
                    "好的示例：「帮我看看明年运势」、「2025年我的运气怎么样」、「想了解一下未来一年的发展」",
                    "避免：复制 instructions 内容——那是用于执行的，不是匹配的",
                  ]
                ).map((tip: string, index: number) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>

            {/* Semantic Matching Note */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2">
                {skillsT?.semanticMatchingNote || "语义匹配详情"}
              </h4>
              <ul className="text-amber-700 text-xs space-y-1 list-disc list-inside">
                {(
                  skillsT?.semanticMatchingDetails || [
                    "使用系统配置的 TEI 服务（BAAI/bge-m3）进行 embedding",
                    "name、description 和 example_queries 都会被索引到 Qdrant",
                    "instructions 不参与匹配（它定义执行行为）",
                    "保存配置时会自动重新索引 embeddings",
                    "阈值控制最低相似度分数（默认 0.75 = 75% 相似）",
                    "每个技能可匹配多个文本，只使用最高分数",
                  ]
                ).map((detail: string, index: number) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Card>

      {/* Validation Status Banner */}
      {skills.length > 0 && (errors.length > 0 || warnings.length > 0) && (
        <Card
          className={`p-4 ${errors.length > 0 ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}
        >
          <div className="flex items-start gap-3">
            {errors.length > 0 ? (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h4
                className={`font-medium ${errors.length > 0 ? "text-red-800" : "text-amber-800"}`}
              >
                {errors.length > 0
                  ? (
                      skillsT?.validationErrors || "发现 {count} 个验证错误"
                    ).replace("{count}", String(errors.length))
                  : (skillsT?.validationWarnings || "{count} 个警告").replace(
                      "{count}",
                      String(warnings.length),
                    )}
              </h4>
              <ul
                className={`mt-2 text-sm space-y-1 ${errors.length > 0 ? "text-red-700" : "text-amber-700"}`}
              >
                {errors.slice(0, 3).map((err, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-mono text-xs bg-white/50 px-1 rounded">
                      {err.skill_id || skillsT?.globalSettings || "全局"}
                    </span>
                    <span>{err.message}</span>
                  </li>
                ))}
                {warnings.slice(0, errors.length > 0 ? 0 : 3).map((warn, i) => (
                  <li key={`w-${i}`} className="flex items-start gap-2">
                    <span className="font-mono text-xs bg-white/50 px-1 rounded">
                      {warn.skill_id || skillsT?.globalSettings || "全局"}
                    </span>
                    <span>{warn.message}</span>
                  </li>
                ))}
                {(errors.length > 3 ||
                  (errors.length === 0 && warnings.length > 3)) && (
                  <li className="text-xs opacity-75">
                    {(skillsT?.andMore || "...还有 {count} 项").replace(
                      "{count}",
                      String(
                        errors.length > 3
                          ? errors.length - 3
                          : warnings.length - 3,
                      ),
                    )}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Skills List and Editor */}
      <div
        className={`grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[600px] ${!skillsEnabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        {/* Left: Skill List */}
        <Card className="p-4 lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              <h3 className="font-medium text-gray-900">
                {skillsT?.title || "技能"}
              </h3>
              <span className="text-sm text-gray-500">({skills.length})</span>
              {/* Validation Status Indicator */}
              {skills.length > 0 &&
                (isValidating ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : isValid && warnings.length === 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : !isValid ? (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                ))}
            </div>
            <div className="flex items-center gap-2">
              {/* Global Settings Button */}
              {onDedupConfigChange && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSettingsDrawerOpen(true)}
                  title={skillsT?.globalSettings || "全局设置"}
                  className="p-1.5"
                >
                  <Settings2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleAddSkill}
                disabled={!skillsEnabled}
              >
                <Plus className="w-4 h-4 mr-1" />
                {skillsT?.add || "添加技能"}
              </Button>
            </div>
          </div>

          {skills.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{skillsT?.noSkills || "暂无技能"}</p>
              <p className="text-xs mt-1">
                {skillsT?.noSkillsHint || "添加技能以启用动态指令注入"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Ungrouped skills first */}
              {groupedSkills["_ungrouped"] && (
                <div className="space-y-2">
                  {groupedSkills["_ungrouped"].map((skill) => (
                    <SkillListItem
                      key={skill.skill_id}
                      skill={skill}
                      isSelected={selectedSkillId === skill.skill_id}
                      onClick={() => setSelectedSkillId(skill.skill_id)}
                      t={skillsT || {}}
                      errorCount={getSkillErrors(skill.skill_id).length}
                      warningCount={getSkillWarnings(skill.skill_id).length}
                    />
                  ))}
                </div>
              )}

              {/* Grouped skills */}
              {Object.entries(groupedSkills)
                .filter(([group]) => group !== "_ungrouped")
                .map(([group, groupSkills]) => (
                  <div key={group}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="text-xs text-gray-500 font-medium px-2">
                        {skillsT?.exclusionGroup || "互斥组"}: {group}
                      </span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>
                    <div className="space-y-2">
                      {groupSkills.map((skill) => (
                        <SkillListItem
                          key={skill.skill_id}
                          skill={skill}
                          isSelected={selectedSkillId === skill.skill_id}
                          onClick={() => setSelectedSkillId(skill.skill_id)}
                          t={skillsT || {}}
                          errorCount={getSkillErrors(skill.skill_id).length}
                          warningCount={getSkillWarnings(skill.skill_id).length}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Card>

        {/* Right: Skill Editor */}
        <div className="lg:col-span-2">
          {selectedSkill ? (
            <SkillEditor
              skill={selectedSkill}
              onChange={(updates) =>
                handleUpdateSkill(selectedSkillId!, updates)
              }
              onDelete={() => handleDeleteSkill(selectedSkillId!)}
              agentContext={agentContext}
              dedupEnabled={dedupConfig?.enabled ?? false}
              globalDedupDefaults={{
                data_query_hours: dedupConfig?.data_query_hours ?? undefined,
                report_window_hours:
                  dedupConfig?.report_window_hours ?? undefined,
                max_per_window: dedupConfig?.max_per_window ?? undefined,
                output_preview_length:
                  dedupConfig?.output_preview_length ?? undefined,
                show_user_query: dedupConfig?.show_user_query ?? undefined,
                query_preview_length:
                  dedupConfig?.query_preview_length ?? undefined,
              }}
            />
          ) : (
            <Card className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
              <Zap className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">
                {skillsT?.noSelection || "选择技能"}
              </p>
              <p className="text-sm mt-1">
                {skillsT?.noSelectionHint ||
                  "从列表中选择一个技能进行编辑，或添加新技能"}
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Global Settings Drawer */}
      {onDedupConfigChange && (
        <SkillGlobalSettingsDrawer
          open={settingsDrawerOpen}
          onClose={() => setSettingsDrawerOpen(false)}
          dedupConfig={dedupConfig || {}}
          onDedupConfigChange={onDedupConfigChange}
          skillTrackingEnabled={skillTrackingEnabled}
        />
      )}
    </div>
  );
};

// TagInput: text input that adds tags on Enter, shown as removable pills
const TagInput: React.FC<{
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}> = ({ tags, onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      if (!tags.includes(input.trim())) {
        onChange([...tags, input.trim()]);
      }
      setInput('');
    }
  };

  const handleRemove = (tag: string) => {
    onChange(tags.filter(t => t !== tag));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
            {tag}
            <button onClick={() => handleRemove(tag)} className="hover:text-red-500 text-xs font-bold">&times;</button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );
};

// PatternListInput: list of regex patterns with add/remove and validation
const PatternListInput: React.FC<{
  patterns: string[];
  onChange: (patterns: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  invalidLabel?: string;
}> = ({ patterns, onChange, placeholder, addLabel = "Add", invalidLabel = "Invalid regex" }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    if (!input.trim()) return;
    try {
      new RegExp(input.trim());
      setError('');
      if (!patterns.includes(input.trim())) {
        onChange([...patterns, input.trim()]);
      }
      setInput('');
    } catch {
      setError(invalidLabel);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      {patterns.map((pattern, idx) => (
        <div key={idx} className="flex items-center gap-2 mb-1.5">
          <code className="flex-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded font-mono text-gray-800 dark:text-gray-200 truncate">
            {pattern}
          </code>
          <button
            onClick={() => onChange(patterns.filter((_, i) => i !== idx))}
            className="text-xs text-red-500 hover:text-red-700 flex-shrink-0"
          >&times;</button>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500"
        />
        <button
          onClick={handleAdd}
          className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 flex-shrink-0"
        >{addLabel}</button>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

// Skill list item component
interface SkillListItemTranslations {
  enabled?: string;
  disabled?: string;
  priority?: string;
  keywordsCount?: string;
  examplesCount?: string;
  errorsCount?: string;
  warningsCount?: string;
}

interface SkillListItemProps {
  skill: SkillConfig;
  isSelected: boolean;
  onClick: () => void;
  t: SkillListItemTranslations;
  errorCount?: number;
  warningCount?: number;
}

const SkillListItem: React.FC<SkillListItemProps> = ({
  skill,
  isSelected,
  onClick,
  t,
  errorCount = 0,
  warningCount = 0,
}) => (
  <div
    className={`p-3 rounded-lg cursor-pointer border transition-colors ${
      isSelected
        ? "border-purple-500 bg-purple-50"
        : errorCount > 0
          ? "border-red-300 bg-red-50 hover:bg-red-100"
          : warningCount > 0
            ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
            : "border-gray-200 hover:bg-gray-50"
    } ${!skill.enabled ? "opacity-60" : ""}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        {/* Validation indicator */}
        {errorCount > 0 ? (
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        ) : warningCount > 0 ? (
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
        ) : null}
        <span className="font-medium text-gray-900 truncate">{skill.name}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {skill.force_response_type && (
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
            {skill.force_response_type}
          </span>
        )}
        <span
          className={`px-2 py-0.5 text-xs rounded ${
            skill.enabled
              ? "bg-emerald-100 text-emerald-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {skill.enabled ? t.enabled || "已启用" : t.disabled || "已禁用"}
        </span>
      </div>
    </div>
    <div className="text-xs text-gray-500 mt-1 font-mono truncate">
      {skill.skill_id}
    </div>
    {skill.description && (
      <p className="text-sm text-gray-500 mt-1 truncate">{skill.description}</p>
    )}
    <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
      <span>
        {t.priority || "优先级"}: {skill.priority}
      </span>
      <span>•</span>
      <span>
        {(t.keywordsCount || "{count} 关键词").replace(
          "{count}",
          String(skill.keywords.length),
        )}
      </span>
      <span>•</span>
      <span>
        {(t.examplesCount || "{count} 示例").replace(
          "{count}",
          String(skill.example_queries.length),
        )}
      </span>
      {/* Validation counts */}
      {(errorCount > 0 || warningCount > 0) && (
        <>
          <span>•</span>
          {errorCount > 0 && (
            <span className="text-red-500">
              {(t.errorsCount || "{count} 错误").replace(
                "{count}",
                String(errorCount),
              )}
            </span>
          )}
          {warningCount > 0 && errorCount === 0 && (
            <span className="text-amber-500">
              {(t.warningsCount || "{count} 警告").replace(
                "{count}",
                String(warningCount),
              )}
            </span>
          )}
        </>
      )}
    </div>
  </div>
);

export default SkillsTab;
